import { apiFetch, buildQueryString } from "./client";
import type { Asset, AssetKind } from "@/types/asset";

/**
 * Upload a raw asset file. The backend takes the bytes as the request body
 * (Content-Type = the file's mime) with `filename` + `kind` as query params,
 * parses/validates them server-side, and returns the created Asset. Team
 * scoping is applied automatically from the auth headers apiFetch sends.
 *
 *   - fonts:      ttf/otf/woff/woff2, <=8MB — server extracts family/weight/style
 *   - reference:  png/jpg/jpeg/webp/gif/svg, <=15MB — expires in 7 days
 *
 * We set Content-Type explicitly so apiFetch doesn't default it to
 * application/json (its rule only fills it when unset), and pass the File
 * directly as the body.
 */
export async function uploadAsset(
  file: File,
  kind: AssetKind,
): Promise<Asset & { derived?: Asset[] }> {
  const qs = buildQueryString({ filename: file.name, kind });
  // Video references (mp4/webm/mov) come back with `derived`: the frame
  // grid + key frames the server extracted — attach THOSE to prompts, the
  // model can't watch the video itself.
  return apiFetch<Asset & { derived?: Asset[] }>(`/assets/upload${qs}`, {
    method: "POST",
    headers: { "Content-Type": file.type || "application/octet-stream" },
    body: file,
  });
}

/** Delete an asset (uploader only — enforced server-side). */
export async function deleteAsset(id: string): Promise<void> {
  await apiFetch<void>(`/assets/${id}`, { method: "DELETE" });
}

/** List visible assets (own + team) of a given kind. */
export async function listAssets(kind: AssetKind): Promise<Asset[]> {
  const qs = buildQueryString({ kind });
  return apiFetch<Asset[]>(`/assets${qs}`);
}
